import '@fullcalendar/core/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import { Client } from '@stomp/stompjs';
import classNames from 'classnames';
import 'font-awesome/css/font-awesome.css';
import 'primeflex/primeflex.css';
import './styles/primeflex-compat.css';
import 'primeicons/primeicons.css';
import { Toast } from 'primereact/toast';
import 'primereact/resources/primereact.min.css';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import React, { Component, Suspense } from 'react';
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import PrimeReact from 'primereact/api';
import { Route, Routes } from 'react-router-dom';
import './App.css';
import './InputSwitchOverride.css';
import './styles/ux-improvements.css';
import './styles/_GenericRecon.css';
import { AppFooter } from './AppFooter';
import { AppInlineProfile } from './AppInlineProfile';
import { AppMenu } from './AppMenu';
import { AppTopbar } from './AppTopbar';
import AppJobMenu from './AppJobMenu';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingFallback from './components/LoadingFallback';
import SkipToContent from './components/SkipToContent';
import axiosClient from './constants';
import { useEffect } from 'react';
import { ElementWrapper } from './components/ElementWrapper';

// Lazy-loaded route components
const About = React.lazy(() => import('./components/About'));
const ComingSoon = React.lazy(() => import('./components/ComingSoon'));
const CustomerCompliance = React.lazy(() => import('./components/CustomerCompliance').then(m => ({ default: m.CustomerCompliance })));
const DashboardBanking = React.lazy(() => import('./components/DashboardBanking'));
const DataQuality = React.lazy(() => import('./components/DataQuality'));
const DataUpload = React.lazy(() => import('./components/DataUpload').then(m => ({ default: m.DataUpload })));
const DeleteData = React.lazy(() => import('./components/DeleteData').then(m => ({ default: m.DeleteData })));
const EmptyPage = React.lazy(() => import('./components/EmptyPage').then(m => ({ default: m.EmptyPage })));
const JobsList = React.lazy(() => import('./components/JobsList'));
const CreateCustomer = React.lazy(() => import('./components/master-data-management/CreateCustomer'));
const CreateVendor = React.lazy(() => import('./components/master-data-management/CreateVendor'));
const SearchCustomers = React.lazy(() => import('./components/master-data-management/SearchCustomers'));
const SearchVendors = React.lazy(() => import('./components/master-data-management/SearchVendors'));
const ViewEditCustomer = React.lazy(() => import('./components/master-data-management/View_EditCustomer'));
const TdscpcCompliance = React.lazy(() => import('./components/master-data-management/TdscpcCompliance'));
const ViewEditVendor = React.lazy(() => import('./components/master-data-management/View_EditVendor'));
const NewFollowUp = React.lazy(() => import('./components/NewFollowUp'));
const NewTCSReconciliation = React.lazy(() => import('./components/NewTCSReconciliation').then(m => ({ default: m.NewTCSReconciliation })));
const NewTDSReconciliation = React.lazy(() => import('./components/NewTDSReconciliation'));
const ParsedTaxBookFromFolder = React.lazy(() => import('./components/ParsedTaxBookFromFolder'));
const Profile = React.lazy(() => import('./components/Profile'));
const PurchaseDataUpload = React.lazy(() => import('./components/PurchaseDataUpload').then(m => ({ default: m.PurchaseDataUpload })));
const PurchaseLoadFromFolder = React.lazy(() => import('./components/PurchaseLoadFromFolder'));
const RawTaxBookFromFolder = React.lazy(() => import('./components/RawTaxBookFromFolder'));
const ReconciliationReports = React.lazy(() => import('./components/ReconciliationReports'));
const ReportsDashboard = React.lazy(() => import('./components/ReportsDashboard').then(m => ({ default: m.ReportsDashboard })));
const SalesDataUpload = React.lazy(() => import('./components/SalesDataUpload').then(m => ({ default: m.SalesDataUpload })));
const SalesLoadFromFolder = React.lazy(() => import('./components/SalesLoadFromFolder'));
const Search26AS = React.lazy(() => import('./components/Search26AS').then(m => ({ default: m.Search26AS })));
const SearchFollowUps = React.lazy(() => import('./components/SearchFollowUps'));
const SearchPurchase = React.lazy(() => import('./components/SearchPurchase').then(m => ({ default: m.SearchPurchase })));
const SearchSales = React.lazy(() => import('./components/SearchSales').then(m => ({ default: m.SearchSales })));
const TCSPrintableView = React.lazy(() => import('./components/TCSPrintableView').then(m => ({ default: m.TCSPrintableView })));
const TDSDiscrepancies = React.lazy(() => import('./components/discrepancies/TDSDiscrepancies'));
const TDSPrintableView = React.lazy(() => import('./components/TDSPrintableView').then(m => ({ default: m.TDSPrintableView })));
const Utilities = React.lazy(() => import('./components/Utilities'));
const ViewFollowUp = React.lazy(() => import('./components/ViewFollowUp'));
const ViewTCSJobs = React.lazy(() => import('./components/ViewTCSJobs'));
const ViewTCSJobWrapper = React.lazy(() => import('./components/ViewTCSJobWrapper'));
const ViewTDSJobs = React.lazy(() => import('./components/ViewTDSJobs'));
const ViewTDSJobWrapper = React.lazy(() => import('./components/ViewTDSJobWrapper'));
const _26AS_Converter = React.lazy(() => import('./components/_26AS_Converter'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const CustomerMappingUploader = React.lazy(() => import('./components/CustomerMappingUploader'));
const CreateCustomerTemplate = React.lazy(() => import('./components/master-data-management/CreateCustomerTemplate'));
const CreateVendorTemplate = React.lazy(() => import('./components/master-data-management/CreateVendorTemplate'));
const SearchExpenseSectionMapping = React.lazy(() => import('./components/34a/SearchExpenseSectionMapping').then(m => ({ default: m.SearchExpenseSectionMapping })));
const SearchExpenseLedger = React.lazy(() => import('./components/34a/SearchExpenseLedger').then(m => ({ default: m.SearchExpenseLedger })));
const SearchSectionExpenseTypeMapping = React.lazy(() => import('./components/34a/SearchSectionExpenseTypeMapping').then(m => ({ default: m.SearchSectionExpenseTypeMapping })));
const ManualReconReports = React.lazy(() => import('./components/ManualReconReports'));
const ReconAssistance = React.lazy(() => import('./components/smartrecon/ReconAssistance'));
const SearchExpense = React.lazy(() => import('./components/SearchExpense').then(m => ({ default: m.SearchExpense })));
const SearchPayment = React.lazy(() => import('./components/SearchPayment').then(m => ({ default: m.SearchPayment })));
const ExpenseUpload = React.lazy(() => import('./components/ExpenseUpload').then(m => ({ default: m.ExpenseUpload })));
const PaymentUpload = React.lazy(() => import('./components/PaymentUpload').then(m => ({ default: m.PaymentUpload })));
const TanITRUpload = React.lazy(() => import('./components/TanITRUpload').then(m => ({ default: m.TanITRUpload })));
const New34AReconciliation = React.lazy(() => import('./components/New34AReconciliation').then(m => ({ default: m.New34AReconciliation })));
const View34aJobs = React.lazy(() => import('./components/View34aJobs'));
const View34aJob = React.lazy(() => import('./components/View34aJob').then(m => ({ default: m.View34aJob })));
const ExpenseClassification = React.lazy(() => import('./components/ExpenseClassification').then(m => ({ default: m.ExpenseClassification })));
const ExpensePrediction = React.lazy(() => import('./components/ExpensePrediction').then(m => ({ default: m.ExpensePrediction })));
const BulkFollowup = React.lazy(() => import('./components/BulkFollowup').then(m => ({ default: m.BulkFollowup })));
const BulkReconAssistance = React.lazy(() => import('./components/smartrecon/BulkReconAssistance'));
const SmartReconPreview = React.lazy(() => import('./components/smartrecon/SmartReconPreview'));
const ExploreTdsPayable = React.lazy(() => import('./components/ExploreTdsPayable').then(m => ({ default: m.ExploreTdsPayable })));
const TDSPayableReportsDashboard = React.lazy(() => import('./components/TDSPayableReportsDashboard'));
const TDSPayableErrorReportsDashboard = React.lazy(() => import('./components/TDSPayableErrorReportsDashboard'));
const LowerTaxDeductionCertificateUpload = React.lazy(() => import('./components/34a/LowerTaxDeductionCertificateUpload').then(m => ({ default: m.LowerTaxDeductionCertificateUpload })));
const _26AS_diff = React.lazy(() => import('./components/_26AS_diff'));
const Form16AUpload = React.lazy(() => import('./components/Form16AUpload').then(m => ({ default: m.Form16AUpload })));
const Form16ASearch = React.lazy(() => import('./components/Form16ASearch').then(m => ({ default: m.Form16ASearch })));
const Link26AS = React.lazy(() => import('./components/Link26AS').then(m => ({ default: m.Link26AS })));
const LinkTanPan = React.lazy(() => import('./components/LinkTanPan').then(m => ({ default: m.LinkTanPan })));
const SearchLinkages26AS = React.lazy(() => import('./components/SearchLinkages26AS').then(m => ({ default: m.SearchLinkages26AS })));
const ManageEntities = React.lazy(() => import('./components/ManageEntities').then(m => ({ default: m.ManageEntities })));
const GstInSearch = React.lazy(() => import('./components/GstInSearch').then(m => ({ default: m.GstInSearch })));
const HelpVideos = React.lazy(() => import('./components/HelpVideos').then(m => ({ default: m.HelpVideos })));
const RawExpenseLedgerUpload = React.lazy(() => import('./components/RawExpenseLedgerUpload').then(m => ({ default: m.RawExpenseLedgerUpload })));
const Reconcile26QFile = React.lazy(() => import('./components/Reconcile26QFile').then(m => ({ default: m.Reconcile26QFile })));
const RuleSetForm = React.lazy(() => import('./components/34a/RuleSetForm').then(m => ({ default: m.RuleSetForm })));
const NWayReconciliation = React.lazy(() => import('./components/nwayrecon/NWayReconciliation'));
const QuickTdsRecon = React.lazy(() => import('./components/quickrecon/QuickTdsRecon'));
const NewGenericReconRun = React.lazy(() => import('./components/genericrecon/NewGenericReconRun'));
const GenericReconResults = React.lazy(() => import('./components/genericrecon/GenericReconResults'));
const GenericReconHistory = React.lazy(() => import('./components/genericrecon/GenericReconHistory'));


class App extends Component {

	constructor() {
		super();
		this.state = {
			layoutMode: 'static',
			profileMode: 'inline',
			layoutCompact: true,
			overlayMenuActive: false,
			staticMenuDesktopInactive: false,
			staticMenuMobileActive: false,
			rotateMenuButton: false,
			topbarMenuActive: false,
			activeTopbarItem: null,
			darkMenu: true,
			menuActive: false,
			theme: 'purple',
			layout: 'purple',
			version: 'v4',
			configDialogActive: false,
			features: {},
			downloadingMenu: null
		};

		this.onDocumentClick = this.onDocumentClick.bind(this);
		this.onMenuClick = this.onMenuClick.bind(this);
		this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
		this.onTopbarMenuButtonClick = this.onTopbarMenuButtonClick.bind(this);
		this.onTopbarItemClick = this.onTopbarItemClick.bind(this);
		this.onMenuItemClick = this.onMenuItemClick.bind(this);
		this.onRootMenuItemClick = this.onRootMenuItemClick.bind(this);
		this.changeMenuMode = this.changeMenuMode.bind(this);
		this.changeMenuColor = this.changeMenuColor.bind(this);
		this.changeProfileMode = this.changeProfileMode.bind(this);
		this.changeVersion = this.changeVersion.bind(this);
		this.changeLayout = this.changeLayout.bind(this);
		this.changeTheme = this.changeTheme.bind(this);
		this.onConfigButtonClick = this.onConfigButtonClick.bind(this);
		this.onConfigCloseClick = this.onConfigCloseClick.bind(this);
		this.onConfigClick = this.onConfigClick.bind(this);
		this.showMessage = this.showMessage.bind(this);
		this.closeSidebar = this.closeSidebar.bind(this);
		this.createMenu();
	}

	webSocketBaseUrl() {
		var l = window.location;
		return ((l.protocol === "https:") ? "wss://" : "ws://") + l.hostname + (((l.port !== 80) && (l.port !== 443)) ? ":" + l.port : "") + "/tax-reco";
	}

	componentDidMount() {
		this.wsReconnectDelay = 1000;
		this.wsMaxReconnectDelay = 30000;
		this.connectWebSocket();
		this.fetchFeatureFlags();
	}

	fetchFeatureFlags() {
		axiosClient.get('/config/features')
			.then(response => {
				this.setState({ features: response.data }, () => this.createMenu());
			})
			.catch(err => {
				console.warn('Failed to fetch feature flags', err);
			});
	}

	componentWillUnmount() {
		if (this.wsReconnectTimer) {
			clearTimeout(this.wsReconnectTimer);
		}
		if (this.wsSubscription) {
			this.wsSubscription.unsubscribe();
		}
		if (this.client) {
			this.client.deactivate();
		}
	}

	connectWebSocket() {
		// Deactivate any existing client to prevent duplicate subscriptions
		if (this.client) {
			this.client.deactivate();
		}

		this.client = new Client();
		let wsBrokerBaseUrl = process.env.REACT_APP_WS_BASE_URL;
		if (process.env.REACT_APP_ENV === 'prod') {
			wsBrokerBaseUrl = this.webSocketBaseUrl();
		}

		this.client.configure({
			brokerURL: wsBrokerBaseUrl + "/stomp",
			reconnectDelay: 0, // Disable built-in reconnection; we handle it manually
			onConnect: () => {
				console.log('WebSocket connected');
				this.wsReconnectDelay = 1000;
				this.wsSubscription = this.client.subscribe('/topic/' + localStorage.userName, message => {
					this.showMessage(message);
				});
			},
			onStompError: (frame) => {
				console.error('STOMP error:', frame.headers['message']);
				this.scheduleReconnect();
			},
			onWebSocketClose: () => {
				console.log('WebSocket disconnected');
				this.scheduleReconnect();
			},
		});

		this.client.activate();
	}

	scheduleReconnect() {
		if (this.wsReconnectTimer) return;
		console.log(`WebSocket reconnecting in ${this.wsReconnectDelay / 1000}s...`);
		this.wsReconnectTimer = setTimeout(() => {
			this.wsReconnectTimer = null;
			this.wsReconnectDelay = Math.min(this.wsReconnectDelay * 2, this.wsMaxReconnectDelay);
			this.connectWebSocket();
		}, this.wsReconnectDelay);
	}

	showMessage(_msg) {
		if (this.growl && _msg) {
			let msg = JSON.parse(_msg.body);
			console.log(msg);
			if (!msg.msgType && !msg.msg) return;
			this.growl.show({
				severity: msg.msgType,
				summary: msg.msg,
				detail: msg.detail
			});
		}
	}

	onMenuClick(event) {
		this.menuClick = true;
	}

	onMenuButtonClick(event) {
		this.menuClick = true;
		this.setState(({
			rotateMenuButton: !this.state.rotateMenuButton,
			topbarMenuActive: false
		}));

		if (this.state.layoutMode === 'overlay') {
			this.setState({
				overlayMenuActive: !this.state.overlayMenuActive
			});
		}
		else {
			if (this.isDesktop())
				this.setState({ staticMenuDesktopInactive: !this.state.staticMenuDesktopInactive });
			else
				this.setState({ staticMenuMobileActive: !this.state.staticMenuMobileActive });
		}

		event && event.preventDefault();
	}
	closeSidebar(event) {
		this.menuClick = true;
		this.setState(({
			rotateMenuButton: true,
			topbarMenuActive: false
		}));

		if (this.state.layoutMode === 'overlay') {
			this.setState({
				overlayMenuActive: false
			});
		}
		else {
			if (this.isDesktop())
				this.setState({ staticMenuDesktopInactive: true });
			else
				this.setState({ staticMenuMobileActive: false });
		}

		event && event.preventDefault();
	}
	onTopbarMenuButtonClick(event) {
		this.topbarItemClick = true;
		this.setState({ topbarMenuActive: !this.state.topbarMenuActive });
		this.hideOverlayMenu();
		event.preventDefault();
	}

	onTopbarItemClick(event) {
		this.topbarItemClick = true;

		if (this.state.activeTopbarItem === event.item)
			this.setState({ activeTopbarItem: null });
		else
			this.setState({ activeTopbarItem: event.item });

		event.originalEvent.preventDefault();
	}

	onMenuItemClick(event) {
		if (!event.item.items) {
			this.hideOverlayMenu();
		}
		if (!event.item.items && this.isHorizontal()) {
			this.setState({
				menuActive: false
			})
		}
	}

	onRootMenuItemClick(event) {
		this.setState({
			menuActive: !this.state.menuActive
		});
	}

	onConfigButtonClick(event) {
		this.configClick = true;
		this.setState({ configDialogActive: !this.state.configDialogActive })
	}

	onConfigCloseClick() {
		this.setState({ configDialogActive: false })
	}

	onConfigClick() {
		this.configClick = true;
	}

	onDocumentClick(event) {
		if (!this.topbarItemClick) {
			this.setState({
				activeTopbarItem: null,
				topbarMenuActive: false
			});
		}

		if (!this.menuClick) {
			if (this.isHorizontal() || this.isSlim()) {
				this.setState({
					menuActive: false
				})
			}

			this.hideOverlayMenu();
		}

		if (!this.configClick) {
			this.setState({ configDialogActive: false });
		}

		if (!this.rightPanelClick) {
			this.setState({
				rightPanelActive: false
			})
		}

		this.topbarItemClick = false;
		this.menuClick = false;
		this.configClick = false;
		this.rightPanelClick = false;
	}

	hideOverlayMenu() {
		this.setState({
			rotateMenuButton: false,
			overlayMenuActive: false,
			staticMenuMobileActive: false
		})
	}

	isTablet() {
		let width = window.innerWidth;
		return width <= 1024 && width > 640;
	}

	isDesktop() {
		return window.innerWidth > 1024;
	}

	isMobile() {
		return window.innerWidth <= 640;
	}

	isOverlay() {
		return this.state.layoutMode === 'overlay';
	}

	isHorizontal() {
		return this.state.layoutMode === 'horizontal';
	}

	isSlim() {
		return this.state.layoutMode === 'slim';
	}

	changeMenuMode(event) {
		this.setState({ layoutMode: event.layoutMode })
		if (event.layoutMode === 'horizontal') {
			this.setState({ profileMode: 'top' })
		}
	}

	changeMenuColor(event) {
		this.setState({ darkMenu: event.darkMenu })
	}

	changeProfileMode(event) {
		this.setState({ profileMode: event.profileMode })
	}

	changeVersion(event) {
		this.setState({ version: event.version });
		if (event.version === 'v3') {
			this.changeStyleSheetUrl('layout-css', this.state.layout, 'layout');
			this.changeStyleSheetUrl('theme-css', this.state.theme, 'theme');
		} else {
			this.changeStyleSheetUrl('layout-css', this.state.layout + '-v4', 'layout');
			this.changeStyleSheetUrl('theme-css', this.state.theme + '-v4', 'theme');
		}
	}

	changeLayout(event) {
		this.setState({ layout: event.layout });
		if (this.state.version === 'v3') {
			this.changeStyleSheetUrl('layout-css', event.layout, 'layout');
		} else {
			this.changeStyleSheetUrl('layout-css', event.layout + '-v4', 'layout');
		}

		if (event.special) {
			this.setState({
				darkMenu: true
			})
		}
	}

	changeTheme(event) {
		this.setState({ theme: event.theme });
		if (this.state.version === 'v3') {
			this.changeStyleSheetUrl('theme-css', event.theme, 'theme');
		} else {
			this.changeStyleSheetUrl('theme-css', event.theme + '-v4', 'theme');
		}
	}

	changeStyleSheetUrl(id, value, prefix) {
		let element = document.getElementById(id);
		let urlTokens = element.getAttribute('href').split('/');
		urlTokens[urlTokens.length - 1] = prefix + '-' + value + '.css';
		let newURL = urlTokens.join('/');

		this.replaceLink(element, newURL);
	}

	isIE() {
		return /(MSIE|Trident\/|Edge\/)/i.test(window.navigator.userAgent)
	}

	replaceLink(linkElement, href) {
		if (this.isIE()) {
			linkElement.setAttribute('href', href);
		}
		else {
			const id = linkElement.getAttribute('id');
			const cloneLinkElement = linkElement.cloneNode(true);

			cloneLinkElement.setAttribute('href', href);
			cloneLinkElement.setAttribute('id', id + '-clone');

			linkElement.parentNode.insertBefore(cloneLinkElement, linkElement.nextSibling);

			cloneLinkElement.addEventListener('load', () => {
				linkElement.remove();
				cloneLinkElement.setAttribute('id', id);
			});
		}
	}

	createMenu() {
		const downloadCustomerTan = async () => {
			if (this.state.downloadingMenu) return;
			this.setState({ downloadingMenu: 'customerTan' });
			try {
				const response = await axiosClient({
					url: `/data-admin/download/customer-tan`,
					method: 'get',
					responseType: 'blob',
				});
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', 'CustomerTAN.csv');
				document.body.appendChild(link);
				link.click();
			} finally {
				this.setState({ downloadingMenu: null });
			}
		}



		const downloadExpenseSectionMapping = async () => {
			if (this.state.downloadingMenu) return;
			this.setState({ downloadingMenu: 'expenseSectionMapping' });
			try {
				const response = await axiosClient({
					url: `/expense-section-mapping/`,
					method: 'get',
					responseType: 'blob',
				});
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', 'ExpenseSectionMapping.txt');
				document.body.appendChild(link);
				link.click();
			} finally {
				this.setState({ downloadingMenu: null });
			}
		}
		const downloadCustomerMapping = async () => {
			if (this.state.downloadingMenu) return;
			this.setState({ downloadingMenu: 'customerMapping' });
			try {
				const response = await axiosClient({
					url: `/customer-mapping/`,
					method: 'get',
					responseType: 'blob',
				});
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', 'CustomerMapping.txt');
				document.body.appendChild(link);
				link.click();
			} finally {
				this.setState({ downloadingMenu: null });
			}
		}
		const role = localStorage.getItem('role');
		console.log("role = " + role)
		role === 'ADMIN' || role === 'NORMAL-USER' ?
			this.menu = [
				{
					label: 'Admin', icon: 'fa fa-fw fa-lock', items: [
						{ label: 'Upload Section-Expense Type', icon: 'fa fa-fw fa-upload', to: '/section-expense-type' },
						{ label: 'TDS Section-Expense Type Mapping', icon: 'fa fa-fw fa-search', to: '/search-section-expense-type' }
					], permission: "ADMIN_OPERATIONS"
				},
				{
					label: 'Dashboards', icon: 'fa fa-fw fa-dashboard', items: [
						{ label: 'Home', icon: 'fa fa-fw fa-home', to: '/' },
					]
				},
				{
					label: 'Data Management', icon: 'fa fa-fw fa-folder', items: [
						{
							label: '26AS', icon: 'fa fa-fw fa-folder', items: [
								{ label: 'Upload', icon: 'fa fa-fw fa-upload', to: '/ingest-26as' },
								{ label: 'Search', icon: 'fa fa-fw fa-search', to: '/search-26as' },
								{ label: 'View Linkages', icon: 'fa fa-fw fa-search', to: '/search-linkages-26as' }
							], permission: "DATA_MANAGEMENT_26AS"
						},
						{
							label: 'Sales', icon: 'fa fa-fw fa-folder', items: [
								{ label: 'Upload', icon: 'fa fa-fw fa-upload', to: '/upload-sales' },
								{ label: 'Search', icon: 'fa fa-fw fa-search', to: '/search-sales' }
							], permission: "DATA_MANAGEMENT_SALES"
						},
						{
							label: 'Expense Ledger', icon: 'fa fa-fw fa-folder', items: [
								{ label: 'Raw Expense Ledger Upload', icon: 'fa fa-fw fa-upload', to: '/raw-upload-expense' },
								{ label: 'Upload', icon: 'fa fa-fw fa-upload', to: '/upload-expense' },
								{ label: 'Search', icon: 'fa fa-fw fa-search', to: '/search-expense' }
							], permission: "DATA_MANAGEMENT_EXPENSE"
						},
						{
							label: 'Payment', icon: 'fa fa-fw fa-folder', items: [
								{ label: 'Upload Payments', icon: 'fa fa-fw fa-upload', to: '/upload-payment' },
								{ label: 'Search', icon: 'fa fa-fw fa-search', to: '/search-payment' }
							], permission: "DATA_MANAGEMENT_EXPENSE"
						},
						{
							label: 'TAN-ITR Report', icon: 'fa fa-fw fa-folder', items: [
								{ label: 'Upload TAN-ITR Report', icon: 'fa fa-fw fa-upload', to: '/upload-tan-itr' },
							], permission: "MANAGE_ENTITIES"
						}
					],
					permission: "DATA_MANAGEMENT"
				},
				{
					label: 'Docs Management', icon: 'fa fa-fw fa-folder', items: [
						{
							label: 'Form 16A', icon: 'fa fa-fw fa-file', items: [
								{ label: 'Bulk Upload', icon: 'fa fa-fw fa-upload', to: '/bulk-ingest-form-16-a' },
								{ label: 'Search', icon: 'fa fa-fw fa-search', to: '/search-form-16-a' }
							], permission: "DOCUMENT_MANAGEMENT_FORM_16A"
						}
					],
					permission: "DOCS_MANAGEMENT"
				},
				{
					label: 'Master Data', icon: 'fa fa-fw fa-folder',
					items: [
						{
							label: 'Customers', icon: 'fa fa-fw fa-users', items: [
								{
									label: 'Create Customer',
									icon: 'fa fa-fw fa-user-plus',
									to: '/new-customer'
								},
								{
									label: 'Search Customers',
									icon: 'fa fa-fw fa-search',
									to: '/search-customers'
								},
								{
									label: 'Sync TAN with PAN',
									icon: 'fa fa-fw fa-link',
									to: '/link-tanpan'
								}
							], permission: "MASTERDATA_CUSTOMERS"
						},

						{
							label: 'Vendors', icon: 'fa fa-fw fa-users', items: [
								{
									label: 'Create Vendor',
									icon: 'fa fa-fw fa-user-plus',
									to: '/new-vendor'
								},
								{
									label: 'Search Vendors',
									icon: 'fa fa-fw fa-search',
									to: '/search-vendors'
								},
								{ label: 'LTDC Upload', icon: 'fa fa-fw fa-upload', to: '/upload-ltdc' },
								...(this.state.features?.tdscpcPanVerification ? [{ label: 'TDSCPC PAN Verification', icon: 'fa fa-fw fa-check-circle', to: '/tdscpc-compliance' }] : []),
							], permission: "MASTERDATA_VENDORS"
						},

						{
							label: 'Expense-Section', icon: 'fa fa-fw fa-map-signs', items: [
								{
									label: 'Upload Mapping',
									icon: 'fa fa-fw fa-upload',
									to: '/upload-expense-section-mapping'
								},
								{ label: 'Search', icon: 'fa fa-fw fa-search', to: '/search-expense-section-mapping' },
								{
									label: 'Download Mapping',
									icon: 'fa fa-fw fa-download',
									command: downloadExpenseSectionMapping
								}
							], permission: "MASTERDATA_EXPENSESECTION_MAPPING"
						},

					],
					permission: 'MASTERDATA_MANAGEMENT'
				},
				{
					label: 'Reconciliation', icon: 'fa fa-fw fa-calculator', items: [
						{
							label: 'TDS Receivable', icon: 'fa fa-fw fa-rupee',
							items: [
								{ label: 'Create New', icon: 'fa fa-fw fa-plus-circle', to: '/new-tds-reconciliation' },
								{ label: 'View Existing', icon: 'fa fa-fw fa-search', to: '/view-tds-reconciliation' },
								{
									label: 'Manual Reconciliation', icon: 'fa fa-fw fa-upload', to: '/manual-reconciliation', permission: "RECON_TDS_MANUAL"
								},
								...(this.state.features?.tdsQuickRecon ? [
									{ label: 'Quick TDS Recon', icon: 'fa fa-fw fa-bolt', to: '/quick-tds-recon' }
								] : [])
							], permission: "RECON_TDS_RECEIVABLE"
						},
						{
							label: 'TDS Payable', icon: 'fa fa-fw fa-rupee',
							items: [
								{ label: 'Create New', icon: 'fa fa-fw fa-plus-circle', to: '/new-34a-reconciliation' },
								{ label: 'Search Existing', icon: 'fa fa-fw fa-search', to: '/view-34a-reconciliation' },
								{ label: 'Reconcile 26Q File', icon: 'fa fa-fw fa-upload', to: '/reconcile-26q-file' },
							], permission: "RECON_TDS_PAYABLE"
						},
						...(this.state.features?.nwayRecon ? [{
							label: 'N-Way Reconciliation', icon: 'fa fa-fw fa-exchange',
							items: [
								{ label: 'New Reconciliation', icon: 'fa fa-fw fa-plus-circle', to: '/nway-reconciliation' },
							], permission: "PERFORM_NWAY_RECONCILIATION"
						}] : []),
						{
							label: 'Generic Reconciliation', icon: 'fa fa-fw fa-exchange',
							items: [
								{ label: 'New Reconciliation', icon: 'fa fa-fw fa-plus-circle', to: '/generic-recon/new' },
								{ label: 'Run History', icon: 'fa fa-fw fa-history', to: '/generic-recon' },
							],
							permission: 'GENERIC_RECON'
						}
					],
				},
				{
					label: 'Follow ups', icon: 'fa fa-fw fa-reply-all', items: [
						// { label: 'Create new', icon: 'fa fa-fw fa-envelope', to: '/new-follow-up' },
						{ label: 'Search Followups', icon: 'fa fa-fw fa-search', to: '/search-follow-ups' }
					]
				},
				{
					label: 'Data Admin', icon: 'fa fa-fw fa-cogs',
					items: [
						{ label: 'Delete Data', icon: 'fa fa-fw fa-trash', to: '/delete-data' },
					], permission: "DATA_ADMIN"
				},
				{
					label: 'Reports', icon: 'fa fa-fw fa-line-chart',
					items: [
						{ label: 'Customer TANs', icon: 'fa fa-fw fa-dashboard', command: downloadCustomerTan },
						{ label: 'Data Quality Reports', icon: 'fa fa-fw fa-dashboard', to: '/data-quality' },
						{ label: 'Reconciliation Reports', icon: 'fa fa-fw fa-dashboard', to: '/reconciliation-reports' },
						{ label: 'Miscellaneous Reports', icon: 'fa fa-fw fa-dashboard', to: '/reports-dashboard' },
						{ label: 'TDS Payable Reports', icon: 'fa fa-fw fa-dashboard', to: '/tds-payable-reports' },
						{ label: 'TDS Payable Error', icon: 'fa fa-fw fa-dashboard', to: '/tds-payable-error-reports' }

					]
				},
				{
					label: 'Jobs Management', icon: 'fa fa-fw fa-cogs',
					items: [
						{
							label: 'View All',
							icon: 'fa fa-fw fa-search',
							to: '/jobs/all'
						},
						{ label: 'Manual Recon Jobs', icon: 'fa fa-fw fa-dashboard', to: '/manual-reconciliation-reports' }
					],
					permission: "RECON_TDS_RECEIVABLE"
				},
				{
					label: 'Utilities', icon: 'fa fa-fw fa-cubes', to: '/utilities'
				},
				// {
				// 	label: 'Smart Recon', icon: 'fa fa-fw fa-gear', to: '/smart-recon', permission: "SMART_RECON"
				// },
				{
					label: 'Bulk Smart Recon', icon: 'fa fa-fw fa-gears', to: '/bulk-smart-recon', permission: "SMART_RECON"
				},
				// {
				// 	label: 'Smart Recon Preview', icon: 'fa fa-fw fa-eye', to: '/smart-recon-preview', permission: "SMART_RECON"
				// },
				// {
				// 	label: 'AI Engine (beta)', icon: 'fa fa-fw fa-database',
				// 	items: [
				// 		{
				// 			label: 'Expenses training', icon: 'fa fa-fw fa-rupee', to: '/expense-classification'
				// 		},
				// 		{
				// 			label: 'Expenses prediction', icon: 'fa fa-fw fa-question-circle', to: '/expense-prediction'
				// 		}
				// 	], permission: "AI_ENGINE"
				// },
				{
					label: 'Entity Management', icon: 'fa fa-fw fa-users',
					items: [
						{
							label: 'Manage Entities', icon: 'fa fa-fw fa-user', to: '/manage-entities'
						}
					], permission: "MANAGE_ENTITIES"
				},
				// {
				// 	label: 'GSTin Search', icon: 'fa fa-fw fa-search', to: '/gstin-search'
				// },
				{
					label: 'Help', icon: 'fa fa-fw fa-question-circle', to: '/help'
				},
			]
			:
			this.menu = [
				{
					label: 'Utilities', icon: 'fa fa-fw fa-cubes', to: '/utilities'
				}
			]

	}

	render() {
		let baseURL = process.env.REACT_APP_API_BASE_URL;
		let layoutClassName = classNames('layout-wrapper', {
			'menu-layout-static': this.state.layoutMode !== 'overlay',
			'menu-layout-overlay': this.state.layoutMode === 'overlay',
			'layout-menu-overlay-active': this.state.overlayMenuActive,
			'menu-layout-slim': this.state.layoutMode === 'slim',
			'menu-layout-horizontal': this.state.layoutMode === 'horizontal',
			'layout-menu-static-inactive': this.state.staticMenuDesktopInactive,
			'layout-menu-static-active': this.state.staticMenuMobileActive
		});
		let menuClassName = classNames('layout-menu-container', { 'layout-menu-dark': this.state.darkMenu });
		const role = localStorage.getItem('role');
		const token = localStorage.getItem('token')

		return (
			<div className={layoutClassName} onClick={this.onDocumentClick} >
				<SkipToContent />
				<Toast ref={(el) => this.growl = el} style={{ marginTop: '30px' }} />
				<div aria-live="polite" className="sr-only" id="route-announcer"></div>

				<AppTopbar profileMode={this.state.profileMode} horizontal={this.isHorizontal()}
					topbarMenuActive={this.state.topbarMenuActive} activeTopbarItem={this.state.activeTopbarItem}
					onMenuButtonClick={this.onMenuButtonClick} onTopbarMenuButtonClick={this.onTopbarMenuButtonClick}
					onTopbarItemClick={this.onTopbarItemClick} />


				<nav className={menuClassName} onClick={this.onMenuClick} aria-label="Main navigation">
					<div className="menu-scroll-content">
						{(this.state.profileMode === 'inline' && this.state.layoutMode !== 'horizontal') && <AppInlineProfile />}
						<AppMenu model={this.menu} onMenuItemClick={this.onMenuItemClick} onRootMenuItemClick={this.onRootMenuItemClick}
							layoutMode={this.state.layoutMode} active={this.state.menuActive} />
					</div>
				</nav>
				<main className="layout-main" id="main-content" role="main">
					<ErrorBoundary>
					<Suspense fallback={<LoadingFallback />}>
					{
						role === 'ADMIN' || role === 'NORMAL-USER' ?
							<>
								{/* <Breadcrumbs separator='>' /> */}
								<Routes>
									<Route path="/" exact element={<DashboardBanking />} />

									<Route exact path="/ingest-26as"
										element={
											<ElementWrapper component={DataUpload}
												name={`26as text file`}
												type={`.txt`}
												apiUrl={baseURL + `/ingest/ingest-26as`} />} />
									<Route exact path="/upload-sales"
										element={
											<ElementWrapper component={SalesDataUpload}
												name={`sales .txt file (as tab separated) or .xls/.xlsx`}
												type={`.txt,.xls,xlsx`}
												apiUrl={baseURL + `/ingest/ingest-sales`} />} />
									<Route exact path="/upload-purchase"
										element={
											<ElementWrapper component={PurchaseDataUpload}
												name={`purchase txt file (as tab separated)`}
												type={`.txt`}
												apiUrl={baseURL + `/ingest/ingest-purchase`} />} />
									<Route exact path="/raw-upload-expense"
										element={
											<ElementWrapper component={RawExpenseLedgerUpload}
												name={`Raw Expense Ledger (as .csv)`}
												type={`.csv`}
												apiUrl={baseURL + `/ingest/transform-raw-expense-ledger`} />} />

									<Route exact path="/upload-expense"
										element={
											<ElementWrapper component={ExpenseUpload}
												name={`Expense Ledger (as .txt)`}
												type={`.txt`}
												apiUrl={baseURL + `/ingest/ingest-expense`} />} />
									<Route exact path="/upload-payment"
										element={
											<ElementWrapper component={PaymentUpload}
												name={`Vendor Payment file (as .txt)`}
												type={`.txt`}
												apiUrl={baseURL + `/ingest/ingest-payment`} />} />
									<Route exact path="/upload-tan-itr"
										element={
											<ElementWrapper component={TanITRUpload}
												name={`TAN-ITR File (as .csv)`}
												type={`.csv`}
												apiUrl={baseURL + `/ingest/ingest-tan-itr-report`} />} />

									<Route exact path="/section-expense-type"
										element={
											<ElementWrapper component={DataUpload}
												name={`Section Expense Type txt file (as tab separated)`}
												type={`.txt`}
												apiUrl={baseURL + `/ingest/ingest-section-expense-type`} />} />

									<Route exact path="/upload-expense-section-mapping"
										element={
											<ElementWrapper component={DataUpload}
												name={`Expense-Section Mapping`}
												type={`.txt`}
												apiUrl={baseURL + `/ingest/ingest-expense-section-mapping`} />}
									/>
									<Route exact path="/manual-reconciliation"
										element={
											<ElementWrapper component={DataUpload}
												info={`Zip file containing the same format as you had downloaded from the transactions report.`}
												name={`Manually Reconciled Entries (zip file)`}
												type={`.zip`}
												apiUrl={baseURL + `/ingest/ingest-manual-reconciliation`} />}
									/>
									<Route exact path="/upload-ltdc"
										element={
											<ElementWrapper component={LowerTaxDeductionCertificateUpload}
												name={`LTDC txt file (as tab separated)`}
												type={`.txt`}
												apiUrl={baseURL + `/ingest/ingest-ltdc`} />} />
									<Route path="/search-26as" element={<Search26AS />} />
									<Route path="/search-payment" element={<SearchPayment />} />
									<Route path="/search-expense-section-mapping" element={<SearchExpenseSectionMapping />} />
									<Route path="/search-expense" element={<SearchExpense />} />
									<Route path="/search-expense-ledger" element={<ComingSoon />} />
									<Route path="/search-sales" element={<SearchSales />} />
									<Route path="/search-purchase" element={<SearchPurchase />} />
									<Route path="/new-tds-reconciliation" element={<NewTDSReconciliation />} />
									<Route path="/view-tds-reconciliation" element={<ViewTDSJobs />} />
									<Route path="/tds-discrepancies" element={<TDSDiscrepancies />} />
									<Route path="/tds-printable-view" element={<TDSPrintableView />} />
									<Route path="/tcs-printable-view" element={<TCSPrintableView />} />
									<Route path="/tds-job/*" element={<ViewTDSJobWrapper closeSidebar={this.closeSidebar} />} />
									<Route path="/tcs-job/*" element={<ViewTCSJobWrapper closeSidebar={this.closeSidebar} />} />
									<Route path="/new-tcs-reconciliation" element={<NewTCSReconciliation redirectToViewJobs={true} />} />
									<Route path="/view-tcs-reconciliation" element={<ViewTCSJobs />} />
									<Route path="/new-34a-reconciliation" element={<New34AReconciliation />} />
									<Route path="/delete-data" element={<DeleteData />} />
									<Route path="/search-section-expense-type" element={<SearchSectionExpenseTypeMapping />} />
									<Route path="/view-34a-reconciliation" element={<View34aJobs />} />
									<Route path="/view-34a-reconciliation-job" element={<View34aJob />} />
									<Route path="/reconcile-26q-file" element={<Reconcile26QFile />} />
									{this.state.features?.nwayRecon && <Route path="/nway-reconciliation" element={<NWayReconciliation />} />}
									{this.state.features?.tdsQuickRecon && <Route path="/quick-tds-recon" element={<QuickTdsRecon />} />}
									<Route exact path="/generic-recon" element={<ElementWrapper component={GenericReconHistory} />} />
									<Route exact path="/generic-recon/new" element={<ElementWrapper component={NewGenericReconRun} />} />
									<Route exact path="/generic-recon/:jobId" element={<ElementWrapper component={GenericReconResults} />} />
									<Route path="/view-34a-output-annual" element={<View34aJobs />} />
									<Route path="/customer-compliance" element={<CustomerCompliance />} />
									<Route path="/customer/:customerId" element={<ViewEditCustomer />} />
									<Route path="/vendor/:vendorId" element={<ViewEditVendor />} />
									<Route path="/search-customers" element={<SearchCustomers />} />
									<Route path="/search-vendors" element={<SearchVendors />} />
									{/* <Route path="/new-customer" element={<CreateCustomer />} /> */}
									<Route exact path="/new-customer"
										element={
											<ElementWrapper component={CreateCustomer} />} />
									<Route path="/transformation-rule" element={<RuleSetForm />} />
									<Route path="/new-customer-template" element={<ElementWrapper component={CreateCustomerTemplate} />} />
									<Route path="/new-vendor-template" element={<ElementWrapper component={CreateVendorTemplate} />} />
									<Route path="/new-vendor" element={<ElementWrapper component={CreateVendor} />} />
									{this.state.features?.tdscpcPanVerification && <Route path="/tdscpc-compliance" element={<TdscpcCompliance />} />}
									<Route path="/new-follow-up" element={<NewFollowUp />} />
									<Route path="/search-follow-ups" element={<SearchFollowUps />} />
									<Route path="/follow-up" element={<ViewFollowUp />} />
									<Route path="/about" element={<About />} />
									<Route path="/empty" element={<EmptyPage />} />
									<Route path="/sales-folder-ingest" element={<SalesLoadFromFolder />} />
									<Route path="/purchase-folder-ingest" element={<PurchaseLoadFromFolder />} />
									<Route path="/parsed-26as-folder-ingest" element={<ParsedTaxBookFromFolder />} />
									<Route path="/raw-26as-folder-ingest" element={<RawTaxBookFromFolder />} />
									<Route path="/profile" element={<Profile />} />
									<Route path="/jobs/all" element={<JobsList />} />
									<Route path="/data-quality" element={<DataQuality />} />
									<Route path="/reconciliation-reports" element={<ReconciliationReports />} />
									<Route path="/tds-payable-reports" element={<TDSPayableReportsDashboard />} />
									<Route path="/tds-payable-error-reports" element={<TDSPayableErrorReportsDashboard />} />
									<Route path="/reports-dashboard" element={<ElementWrapper component={ReportsDashboard} />} />
									<Route path="/utilities" element={<Utilities />} />
									<Route path="/26as_converter" element={<_26AS_Converter />} />
									<Route path="/26as_diff" element={<_26AS_diff />} />
									<Route path="/not-found" element={<NotFound />} />
									<Route path="/upload-customer-mapping" element={<CustomerMappingUploader />} />
									<Route path="/manual-reconciliation-reports" element={<ElementWrapper component={ManualReconReports} />} />
									<Route path="/smart-recon" element={<ElementWrapper component={ReconAssistance} />} />
									<Route path="/bulk-smart-recon" element={<ElementWrapper component={BulkReconAssistance} />} />
									<Route path="/smart-recon-preview" element={<ElementWrapper component={SmartReconPreview} />} />
									<Route path="/expense-classification" element={<ExpenseClassification />} />
									<Route path="/expense-prediction" element={<ExpensePrediction />} />
									<Route path="/bulk-follow-up" element={<ElementWrapper component={BulkFollowup} />} />
									<Route path="/tds-payable/explore" element={<ExploreTdsPayable />} />
									<Route path="/ingest-form-16-a" element={<Form16AUpload />} />
									<Route exact path="/bulk-ingest-form-16-a"
										element={
											<ElementWrapper component={DataUpload}
												info={`The Zip file must contain one or more PDFs with the file name in this format: PanNo_Quarter_FinancialYear (eg: OLQCH5188B_Q1_2021-22.pdf)`}
												name={`Zip file containing Form 16A PDFs (zip file)`}
												type={`.zip`}
												apiUrl={baseURL + `/ingest/bulk-form-16a`} />}
									/>
									<Route path="/search-form-16-a" element={<Form16ASearch />} />
									<Route path="/link-26as" element={<Link26AS />} />
									<Route path="/link-tanpan" element={<LinkTanPan />} />
									<Route path="/search-linkages-26as" element={<SearchLinkages26AS />} />
									<Route path="/manage-entities" element={<ManageEntities />} />
									{/* <Route path="/gstin-search" element={<GstInSearch />} /> */}
									<Route path="/help" element={<HelpVideos />} />

								</Routes>
							</>
							:
							<>
								<Routes>
									<Route path="/utilities" element={<Utilities />} />
									<Route path="/profile" element={<Profile />} />
									<Route path="/26as_converter" element={<_26AS_Converter />} />
								</Routes>
							</>
					}
					</Suspense>
					</ErrorBoundary>
				</main>

				<div className="layout-mask">
				</div>
				<AppJobMenu
					style={{ top: '230px' }}
				/>
				<AppFooter />
			</div>
		);
	}
}

export default App;
